import { Container, Graphics } from "pixi.js";

interface Particle {
  gfx: Graphics;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  scale: number;
  scaleSpeed: number;
  rotationSpeed: number;
  gravity: number;
  color: number;
}

export class ParticleSystem extends Container {
  private particles: Particle[] = [];
  private animating = false;

  spawnBurst(
    x: number,
    y: number,
    count: number,
    opts: {
      color?: number;
      colors?: number[];
      speedMin?: number;
      speedMax?: number;
      lifeMin?: number;
      lifeMax?: number;
      sizeMin?: number;
      sizeMax?: number;
      gravity?: number;
      shape?: "circle" | "square" | "star";
    } = {}
  ) {
    const {
      colors = opts.color ? [opts.color] : [0xffffff],
      speedMin = 1,
      speedMax = 4,
      lifeMin = 20,
      lifeMax = 45,
      sizeMin = 2,
      sizeMax = 5,
      gravity = 0.1,
      shape = "circle",
    } = opts;

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = speedMin + Math.random() * (speedMax - speedMin);
      const size = sizeMin + Math.random() * (sizeMax - sizeMin);
      const life = lifeMin + Math.random() * (lifeMax - lifeMin);
      const color = colors[Math.floor(Math.random() * colors.length)];

      const gfx = new Graphics();
      if (shape === "circle") {
        gfx.circle(0, 0, size);
      } else if (shape === "square") {
        gfx.rect(-size / 2, -size / 2, size, size);
      } else {
        // star
        this.drawStar(gfx, 0, 0, 5, size, size * 0.5);
      }
      gfx.fill({ color });
      gfx.x = x;
      gfx.y = y;

      this.addChild(gfx);

      this.particles.push({
        gfx,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life,
        maxLife: life,
        scale: 1,
        scaleSpeed: 0.02 + Math.random() * 0.03,
        rotationSpeed: (Math.random() - 0.5) * 0.3,
        gravity,
        color,
      });
    }

    if (!this.animating) {
      this.animating = true;
      this.tick();
    }
  }

  spawnConfetti(x: number, y: number, count = 60) {
    const confettiColors = [
      0xff6b6b, 0x4ecdc4, 0x45b7d1, 0xf9ca24, 0xf0932b,
      0xeb4d4b, 0x6c5ce7, 0xa29bfe, 0xfd79a8, 0xe17055,
    ];
    this.spawnBurst(x, y, count, {
      colors: confettiColors,
      speedMin: 3,
      speedMax: 8,
      lifeMin: 40,
      lifeMax: 80,
      sizeMin: 3,
      sizeMax: 7,
      gravity: 0.15,
      shape: "square",
    });
  }

  spawnDust(x: number, y: number, color = 0xd4c5a0) {
    this.spawnBurst(x, y, 8, {
      color,
      speedMin: 0.5,
      speedMax: 2,
      lifeMin: 15,
      lifeMax: 30,
      sizeMin: 1.5,
      sizeMax: 3,
      gravity: 0.05,
      shape: "circle",
    });
  }

  spawnSparkle(x: number, y: number, color = 0x4ade80) {
    this.spawnBurst(x, y, 12, {
      color,
      speedMin: 1,
      speedMax: 4,
      lifeMin: 20,
      lifeMax: 40,
      sizeMin: 2,
      sizeMax: 4,
      gravity: -0.05,
      shape: "star",
    });
  }

  spawnSmoke(x: number, y: number, color = 0x666666) {
    this.spawnBurst(x, y, 10, {
      color,
      speedMin: 0.3,
      speedMax: 1.5,
      lifeMin: 25,
      lifeMax: 50,
      sizeMin: 3,
      sizeMax: 6,
      gravity: -0.02,
      shape: "circle",
    });
  }

  private drawStar(g: Graphics, cx: number, cy: number, points: number, outer: number, inner: number) {
    g.moveTo(cx + outer, cy);
    for (let i = 1; i < points * 2; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = (Math.PI * i) / points;
      g.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    }
    g.closePath();
  }

  private tick() {
    if (this.particles.length === 0) {
      this.animating = false;
      return;
    }

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.vx *= 0.98;
      p.vy *= 0.98;
      p.vy += p.gravity;
      p.gfx.x += p.vx;
      p.gfx.y += p.vy;
      p.gfx.rotation += p.rotationSpeed;
      p.life--;

      const alpha = p.life / p.maxLife;
      p.gfx.alpha = alpha;
      p.gfx.scale.set(alpha);

      if (p.life <= 0) {
        this.removeChild(p.gfx);
        p.gfx.destroy();
        this.particles.splice(i, 1);
      }
    }

    requestAnimationFrame(() => this.tick());
  }

  clear() {
    for (const p of this.particles) {
      this.removeChild(p.gfx);
      p.gfx.destroy();
    }
    this.particles = [];
    this.animating = false;
  }
}
